import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Award, 
  Calendar, 
  TrendingUp, 
  FileDown, 
  MessageSquare, 
  Edit, 
  History as HistoryIcon, 
  Trash2, 
  Edit2, 
  Save,
  ClipboardList
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { exportEvaluationToPDF } from "@/lib/pdfExport";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import EvaluationHistory from "@/components/EvaluationHistory";
import { CriteriaInfoCard } from "@/components/CriteriaInfoCard";
import { loadGlobalCriteria } from "@/lib/criteriaLoader";

interface EmployeeEvaluationHistoryTabProps {
  employeeId: string;
  employeeName: string;
}

interface EmployeeNote {
  id: string;
  note: string;
  created_at: string;
  created_by: string;
  profiles: {
    full_name: string;
  };
}

interface SimpleEvaluation {
  id: string;
  month: string;
  date: string;
  totalScore: number;
  maxScore: number;
  performanceLevel: string;
  createdByName: string | null;
}

export function EmployeeEvaluationHistoryTab({ employeeId, employeeName }: EmployeeEvaluationHistoryTabProps) {
  const navigate = useNavigate();
  const { user, userRole, profile } = useAuth();
  const [evaluations, setEvaluations] = useState<SimpleEvaluation[]>([]);
  const [notes, setNotes] = useState<EmployeeNote[]>([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [evaluationToDelete, setEvaluationToDelete] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  
  const isEmployee = userRole === 'employee';
  
  // HR-Rollen können Bewertungen erstellen, aber nicht für sich selbst
  // Wichtig: Beide IDs müssen als Strings verglichen werden und dürfen nicht leer sein
  const currentUserEmployeeId = profile?.employee_id;
  const isOwnProfile = Boolean(currentUserEmployeeId && employeeId && currentUserEmployeeId === employeeId);
  
  // Nur HR-Rollen dürfen Bewertungen erstellen, und nur für ANDERE Mitarbeiter (nicht sich selbst)
  const canEditEvaluations = (userRole === 'admin' || userRole === 'hr_manager' || userRole === 'hr_clerk') && !isOwnProfile;

  useEffect(() => {
    fetchData();
  }, [employeeId, user]);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    await Promise.all([fetchEvaluations(), fetchNotes()]);
    setLoading(false);
  };

  const fetchEvaluations = async () => {
    try {
      const { data: evaluationsData, error: evaluationsError } = await supabase
        .from('evaluations')
        .select(`
          id,
          employee_id,
          month,
          date,
          total_score,
          max_score,
          performance_level,
          created_by,
          profiles!evaluations_created_by_fkey (
            full_name
          )
        `)
        .eq('employee_id', employeeId)
        .order('month', { ascending: false });

      if (evaluationsError) throw evaluationsError;

      const formattedEvaluations = (evaluationsData || []).map(ev => ({
        id: ev.id,
        month: ev.month,
        date: ev.date,
        totalScore: ev.total_score,
        maxScore: ev.max_score,
        performanceLevel: ev.performance_level,
        createdByName: ev.profiles?.full_name || null,
        criteriaRatings: {}
      }));

      setEvaluations(formattedEvaluations);
    } catch (error) {
      console.error('Error fetching evaluations:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('employee_notes')
        .select(`
          id,
          note,
          created_at,
          created_by,
          profiles!inner (
            full_name
          )
        `)
        .eq('employee_id', employeeId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !user) {
      toast.error("Bitte geben Sie eine Notiz ein");
      return;
    }

    try {
      const { error } = await supabase
        .from('employee_notes')
        .insert({
          employee_id: employeeId,
          note: newNote,
          created_by: user.id
        });

      if (error) throw error;

      toast.success("Notiz erfolgreich hinzugefügt");
      setNewNote("");
      fetchNotes();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error("Fehler beim Hinzufügen der Notiz");
    }
  };

  const handleEditNote = (noteId: string, currentText: string) => {
    setEditingNoteId(noteId);
    setEditingNoteText(currentText);
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editingNoteText.trim()) {
      toast.error("Notiz darf nicht leer sein");
      return;
    }

    try {
      const { error } = await supabase
        .from("employee_notes")
        .update({ note: editingNoteText })
        .eq("id", noteId);

      if (error) throw error;

      toast.success("Notiz erfolgreich aktualisiert");
      setEditingNoteId(null);
      setEditingNoteText("");
      fetchNotes();
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error("Fehler beim Aktualisieren der Notiz");
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      const { error } = await supabase
        .from("employee_notes")
        .delete()
        .eq("id", noteToDelete);

      if (error) throw error;

      toast.success("Notiz erfolgreich gelöscht");
      setDeleteNoteDialogOpen(false);
      setNoteToDelete(null);
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error("Fehler beim Löschen der Notiz");
    }
  };

  const getPerformanceBadgeColor = (level: string) => {
    switch (level) {
      case "Überzeugend": return "bg-success text-success-foreground";
      case "Sehr gut": return "bg-chart-3 text-white";
      case "Gut": return "bg-primary text-primary-foreground";
      case "Teilweise gut": return "bg-warning text-warning-foreground";
      default: return "bg-destructive text-destructive-foreground";
    }
  };

  const handleExportEvaluation = async (evaluation: SimpleEvaluation) => {
    // Use global criteria from database (single source of truth)
    const loadedCriteria = await loadGlobalCriteria();
    const employee = {
      id: employeeId,
      name: employeeName,
      position: "",
      employeeNumber: "",
      startDate: ""
    };
    const evaluationForExport = {
      ...evaluation,
      employeeId,
      criteria: {},
      createdByName: evaluation.createdByName || undefined
    };
    exportEvaluationToPDF(employee, evaluationForExport as any, loadedCriteria);
    toast.success("PDF wurde erstellt");
  };

  const handleShowHistory = (evaluationId: string) => {
    setSelectedEvaluationId(evaluationId);
    setHistoryDialogOpen(true);
  };

  const handleEditEvaluation = (evaluationId: string) => {
    navigate(`/matrix?edit=${evaluationId}`);
  };

  const handleDeleteClick = (evaluationId: string) => {
    setEvaluationToDelete(evaluationId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!evaluationToDelete) return;

    try {
      const { error } = await supabase
        .from('evaluations')
        .delete()
        .eq('id', evaluationToDelete);

      if (error) throw error;

      toast.success("Bewertung erfolgreich gelöscht");
      setDeleteDialogOpen(false);
      setEvaluationToDelete(null);
      fetchEvaluations();
    } catch (error) {
      console.error('Error deleting evaluation:', error);
      toast.error("Fehler beim Löschen der Bewertung");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Lade Bewertungshistorie...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Criteria Info Card for Employees */}
      <CriteriaInfoCard />

      {/* Evaluation History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Bewertungshistorie
          </CardTitle>
        </CardHeader>
        <CardContent>
          {evaluations.length === 0 ? (
            <div className="py-8 text-center">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Bewertungen</h3>
              <p className="text-muted-foreground mb-4">
                {isEmployee 
                  ? "Es wurden noch keine Bewertungen für Sie erstellt."
                  : "Erstellen Sie die erste Bewertung für diesen Mitarbeiter."
                }
              </p>
              {canEditEvaluations && (
                <Button onClick={() => navigate(`/evaluate/${employeeId}`)} className="gap-2">
                  <Award className="h-4 w-4" />
                  Bewertung erstellen
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {canEditEvaluations && (
                <div className="flex justify-end">
                  <Button onClick={() => navigate(`/evaluate/${employeeId}`)} className="gap-2">
                    <Award className="h-4 w-4" />
                    Neue Bewertung
                  </Button>
                </div>
              )}
              {evaluations.map((evaluation) => (
                <Card key={evaluation.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-semibold">
                            {new Date(evaluation.month + '-01').toLocaleDateString('de-DE', { 
                              year: 'numeric', 
                              month: 'long' 
                            })}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Bewertet am {new Date(evaluation.date).toLocaleDateString('de-DE')}
                            {evaluation.createdByName && ` von ${evaluation.createdByName}`}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getPerformanceBadgeColor(evaluation.performanceLevel)}`}>
                        {evaluation.performanceLevel}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="flex items-center gap-2 p-2 bg-primary/5 rounded-lg">
                        <TrendingUp className="h-4 w-4 text-primary flex-shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Punkte</p>
                          <p className="font-bold">{evaluation.totalScore.toFixed(1)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-accent/5 rounded-lg">
                        <Award className="h-4 w-4 text-accent flex-shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Max</p>
                          <p className="font-bold">{evaluation.maxScore}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                        <div className="h-4 w-4 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold flex-shrink-0">
                          %
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Prozent</p>
                          <p className="font-bold">
                            {evaluation.maxScore > 0 
                              ? ((evaluation.totalScore / evaluation.maxScore) * 100).toFixed(0) 
                              : 0}%
                          </p>
                        </div>
                      </div>
                    </div>

                    {canEditEvaluations && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-xs"
                          onClick={() => handleEditEvaluation(evaluation.id)}
                        >
                          <Edit className="h-3 w-3" />
                          Bearbeiten
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-xs"
                          onClick={() => handleShowHistory(evaluation.id)}
                        >
                          <HistoryIcon className="h-3 w-3" />
                          Historie
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-xs"
                          onClick={() => handleExportEvaluation(evaluation)}
                        >
                          <FileDown className="h-3 w-3" />
                          PDF
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-xs text-destructive hover:bg-destructive hover:text-destructive-foreground"
                          onClick={() => handleDeleteClick(evaluation.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                          Löschen
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* Notes Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Besprechungsnotizen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {canEditEvaluations && (
            <div className="space-y-2">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Neue Notiz hinzufügen..."
                rows={3}
              />
              <div className="flex justify-end">
                <Button onClick={handleAddNote} disabled={!newNote.trim()} size="sm">
                  Notiz hinzufügen
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {notes.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Noch keine Notizen vorhanden
              </p>
            ) : (
              notes.map((note) => (
                <Card key={note.id} className="bg-muted/30">
                  <CardContent className="pt-4">
                    {editingNoteId === note.id ? (
                      <div className="space-y-3">
                        <Textarea
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          rows={3}
                          className="w-full"
                        />
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            onClick={() => handleUpdateNote(note.id)}
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Speichern
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditingNoteText("");
                            }}
                          >
                            Abbrechen
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-primary">
                            {note.profiles.full_name}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {new Date(note.created_at).toLocaleDateString('de-DE', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {user && user.id === note.created_by && (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleEditNote(note.id, note.note)}
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={() => {
                                    setNoteToDelete(note.id);
                                    setDeleteNoteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      {selectedEvaluationId && (
        <EvaluationHistory
          evaluationId={selectedEvaluationId}
          isOpen={historyDialogOpen}
          onClose={() => {
            setHistoryDialogOpen(false);
            setSelectedEvaluationId(null);
          }}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bewertung löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diese Bewertung löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteNoteDialogOpen} onOpenChange={setDeleteNoteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Notiz löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diese Notiz löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteNote}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
